/**
 * « À faire aujourd'hui » : cinq lignes au lieu d'un chiffre.
 *
 * Le tableau de bord disait « 103 choses à traiter, dont 41 prioritaires ».
 * Un chiffre de 103 ne se lit pas ; une liste de cinq actions se traite. Ce
 * module range les alertes par FAMILLE D'ACTION — ce qu'il y a à faire, pas
 * l'objet qui le porte — et choisit les cinq qui passent en premier.
 *
 * L'ordre : d'abord les familles qui ont de l'urgent, la plus chargée en tête ;
 * puis, sans urgent : ce qui est dû, ce qui est à facturer, les clients signés
 * qui attendent une date, et seulement ensuite ce qui est à vendre. Un chantier
 * signé sans date, c'est un client qui attend ; un devis à relancer, c'est un
 * client qui hésite.
 */

export type FamilleAction =
  | 'factures_a_encaisser'
  | 'chantiers_a_facturer'
  | 'factures_a_emettre'
  | 'factures_a_envoyer'
  | 'chantiers_a_planifier'
  | 'devis_a_relancer'
  | 'devis_a_convertir'
  | 'devis_a_envoyer'
  | 'preparation';

interface Famille {
  /** « 1 devis à relancer » */
  un: string;
  /** « 14 devis à relancer » */
  plusieurs: string;
  /** Comment dire les alertes de priorité haute : « en retard », « urgent »… */
  urgentUn: string;
  urgentPlusieurs: string;
  /**
   * Sur quoi portent les montants, ou `null` si l'argent n'a pas de sens ici.
   * Le bloc mêlait des TTC (devis, factures) et des HT (chantiers à facturer)
   * sans le dire.
   */
  base: 'TTC' | 'HT' | null;
  /**
   * Ce qu'on compte. `objet` : un devis, une facture, un chantier — une seule
   * fois, même s'il porte deux alertes (un devis peut être à la fois « sans
   * réponse » et « expire bientôt » ; il était compté deux fois, et son
   * montant aussi). `alerte` : chaque alerte, quand le libellé compte des
   * étapes et non des objets.
   */
  unite: 'objet' | 'alerte';
  /**
   * La composition de la ligne, par type d'alerte, à la place de « dont N
   * urgents » — quand le libellé seul pourrait tromper.
   */
  detail?: Readonly<Record<string, readonly [string, string]>>;
}

/** Dans l'ordre de départage, quand rien n'est urgent. */
export const FAMILLES: Readonly<Record<FamilleAction, Famille>> = {
  // « Factures à encaisser » laissait lire qu'on ne devait que ce montant-là :
  // la ligne ne compte que ce qui DEMANDE UNE ACTION (retard, échéance de la
  // semaine, solde partiel), pas tout l'encours — c'est la tuile « Encours
  // client » qui le donne.
  factures_a_encaisser: {
    un: 'paiement à suivre',
    plusieurs: 'paiements à suivre',
    urgentUn: 'en retard',
    urgentPlusieurs: 'en retard',
    base: 'TTC',
    unite: 'objet',
    detail: {
      facture_en_retard: ['en retard', 'en retard'],
      facture_echeance: ['à échéance cette semaine', 'à échéance cette semaine'],
      facture_solde: ['solde partiel', 'soldes partiels'],
    },
  },
  chantiers_a_facturer: {
    un: 'chantier terminé à facturer',
    plusieurs: 'chantiers terminés à facturer',
    urgentUn: 'depuis plus d’une semaine',
    urgentPlusieurs: 'depuis plus d’une semaine',
    base: 'HT',
    unite: 'objet',
  },
  factures_a_emettre: {
    un: 'facture en brouillon à émettre',
    plusieurs: 'factures en brouillon à émettre',
    urgentUn: 'urgente',
    urgentPlusieurs: 'urgentes',
    base: 'TTC',
    unite: 'objet',
  },
  factures_a_envoyer: {
    un: 'facture émise à envoyer',
    plusieurs: 'factures émises à envoyer',
    urgentUn: 'urgente',
    urgentPlusieurs: 'urgentes',
    base: 'TTC',
    unite: 'objet',
  },
  chantiers_a_planifier: {
    un: 'chantier à placer à l’agenda',
    plusieurs: 'chantiers à placer à l’agenda',
    urgentUn: 'urgent',
    urgentPlusieurs: 'urgents',
    base: null,
    unite: 'objet',
  },
  devis_a_relancer: {
    un: 'devis à relancer',
    plusieurs: 'devis à relancer',
    urgentUn: 'urgent',
    urgentPlusieurs: 'urgents',
    base: 'TTC',
    unite: 'objet',
  },
  devis_a_convertir: {
    un: 'devis signé à facturer',
    plusieurs: 'devis signés à facturer',
    urgentUn: 'urgent',
    urgentPlusieurs: 'urgents',
    base: 'TTC',
    unite: 'objet',
  },
  devis_a_envoyer: {
    un: 'devis à envoyer',
    plusieurs: 'devis à envoyer',
    urgentUn: 'urgent',
    urgentPlusieurs: 'urgents',
    base: 'TTC',
    unite: 'objet',
  },
  preparation: {
    un: 'étape de préparation à faire',
    plusieurs: 'étapes de préparation à faire',
    urgentUn: 'pour un chantier proche',
    urgentPlusieurs: 'pour des chantiers proches',
    base: null,
    unite: 'alerte',
  },
};

const ORDRE = Object.keys(FAMILLES) as FamilleAction[];

/**
 * Ce texte (lu dans une adresse, par exemple) est-il une famille connue ?
 *
 * `in` ne suffirait pas : « toString » est dans tout objet, par héritage.
 */
export function estFamilleAction(x: string | null): x is FamilleAction {
  return x !== null && (ORDRE as readonly string[]).includes(x);
}

/** La famille d'un type d'alerte, ou `null` s'il n'en a pas. */
export function familleDe(type: string): FamilleAction | null {
  switch (type) {
    case 'facture_en_retard':
    case 'facture_echeance':
    case 'facture_solde':
      return 'factures_a_encaisser';
    case 'facturation_a_realiser':
      return 'chantiers_a_facturer';
    case 'facture_brouillon':
      return 'factures_a_emettre';
    case 'facture_a_envoyer':
      return 'factures_a_envoyer';
    // Un devis expiré ou qui expire se rattrape comme un devis sans réponse :
    // par un appel au client.
    case 'devis_sans_reponse':
    case 'devis_expire':
    case 'devis_expire_bientot':
      return 'devis_a_relancer';
    case 'devis_a_convertir':
      return 'devis_a_convertir';
    case 'devis_a_envoyer':
      return 'devis_a_envoyer';
    case 'chantier_non_planifie':
      return 'chantiers_a_planifier';
    case 'nacelle_a_commander':
      return 'preparation';
    default:
      return type.startsWith('prep_') ? 'preparation' : null;
  }
}

export interface AlerteResumee {
  type: string;
  priorite: 'haute' | 'moyenne' | 'basse';
  montant?: number | undefined;
  /** L'objet qui porte l'alerte : deux alertes du même devis ne font qu'un devis. */
  entite?: string;
  entiteId?: string;
}

export interface LigneAction {
  famille: FamilleAction;
  nombre: number;
  /** Combien sont de priorité haute. */
  urgents: number;
  /** Le total en jeu, ou `null` quand l'argent n'a pas de sens ici. */
  montant: number | null;
  /** HT ou TTC : ce sur quoi porte `montant`. */
  base: 'TTC' | 'HT' | null;
  /** « factures à encaisser » — sans le nombre, pour l'afficher à part. */
  nom: string;
  /** « 14 factures à encaisser » */
  libelle: string;
  /** « dont 3 en retard », « 1 en retard · 2 soldes partiels », ou `null`. */
  precision: string | null;
}

export interface ActionsDuJour {
  lignes: LigneAction[];
  /** Les familles qui existent mais n'ont pas trouvé place dans les lignes. */
  famillesMasquees: number;
  /** Toutes les alertes, pour le lien « tout voir ». */
  total: number;
}

const RANG: Record<AlerteResumee['priorite'], number> = { haute: 0, moyenne: 1, basse: 2 };

/** Un objet de la famille, avec l'alerte qui le décrit le mieux. */
interface Objet {
  priorite: AlerteResumee['priorite'];
  type: string;
  montant: number;
}

export function actionsDuJour(alertes: readonly AlerteResumee[], max = 5): ActionsDuJour {
  const parFamille = new Map<FamilleAction, Map<string, Objet>>();
  alertes.forEach((a, i) => {
    const f = familleDe(a.type);
    if (f === null) return;
    const cle =
      FAMILLES[f].unite === 'objet' && a.entite !== undefined && a.entiteId !== undefined
        ? `${a.entite}:${a.entiteId}`
        : `#${String(i)}`;
    const objets = parFamille.get(f) ?? new Map<string, Objet>();
    const m =
      typeof a.montant === 'number' && Number.isFinite(a.montant) && a.montant > 0 ? a.montant : 0;
    const deja = objets.get(cle);
    if (deja === undefined) {
      objets.set(cle, { priorite: a.priorite, type: a.type, montant: m });
    } else {
      // L'alerte la plus pressante décrit l'objet ; son montant n'est compté
      // qu'une fois — c'est le même argent, vu par deux alertes.
      if (RANG[a.priorite] < RANG[deja.priorite]) {
        deja.priorite = a.priorite;
        deja.type = a.type;
      }
      deja.montant = Math.max(deja.montant, m);
    }
    parFamille.set(f, objets);
  });

  const resumes = new Map<
    FamilleAction,
    { nombre: number; urgents: number; montant: number; types: Map<string, number> }
  >();
  for (const [f, objets] of parFamille) {
    const r = { nombre: 0, urgents: 0, montant: 0, types: new Map<string, number>() };
    for (const o of objets.values()) {
      r.nombre += 1;
      if (o.priorite === 'haute') r.urgents += 1;
      r.montant += o.montant;
      r.types.set(o.type, (r.types.get(o.type) ?? 0) + 1);
    }
    resumes.set(f, r);
  }

  const toutes: LigneAction[] = [...resumes.entries()]
    .sort(
      ([fa, a], [fb, b]) =>
        // L'urgent d'abord, la famille la plus chargée en urgent en tête…
        Number(b.urgents > 0) - Number(a.urgents > 0) ||
        b.urgents - a.urgents ||
        // … puis l'ordre de l'argent.
        ORDRE.indexOf(fa) - ORDRE.indexOf(fb),
    )
    .map(([famille, v]) => {
      const meta = FAMILLES[famille];
      const nom = v.nombre > 1 ? meta.plusieurs : meta.un;
      const detail = meta.detail;
      const composition =
        detail === undefined
          ? null
          : Object.entries(detail)
              .map(([type, [un, plusieurs]]) => {
                const n = v.types.get(type) ?? 0;
                return n === 0 ? null : `${String(n)} ${n > 1 ? plusieurs : un}`;
              })
              .filter((x): x is string => x !== null)
              .join(' · ');
      return {
        famille,
        nombre: v.nombre,
        nom,
        urgents: v.urgents,
        montant: meta.base !== null && v.montant > 0 ? Math.round(v.montant * 100) / 100 : null,
        base: meta.base,
        libelle: `${String(v.nombre)} ${nom}`,
        precision:
          composition !== null && composition !== ''
            ? composition
            : v.urgents === 0
              ? null
              : `dont ${String(v.urgents)} ${v.urgents > 1 ? meta.urgentPlusieurs : meta.urgentUn}`,
      };
    });

  return {
    lignes: toutes.slice(0, max),
    famillesMasquees: Math.max(0, toutes.length - max),
    total: alertes.length,
  };
}
